import type { ContinuationMeta, ContinuationRequest } from '@/hooks/pipelines/continuation';
import { getNextPageParam, getPreviousPageParam } from '@/hooks/pipelines/continuation';
import { useStore } from '@nanostores/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountStore, setAccountStore } from '@/services/auth/store';
import { notifyOnError } from '@/services/cdp-api';
import type { DestinationId } from './api';
import {
  createDestination,
  createDestinationOAuthConnection,
  createDestinationSubscription,
  deleteDestination,
  deleteDestinationOAuthConnection,
  deleteDestinationSubscription,
  getDestination,
  getDestinationErrorById,
  getDestinationErrorSamples,
  getDestinationErrorsSummary,
  getDestinationEventById,
  getDestinationEvents,
  getDestinationMetrics,
  getDestinationOAuthConnection,
  getDestinationOAuthProviderConfig,
  getDestinationOptionManifest,
  getDestinationsLastEvents as getDestinationsLastSeen,
  getDestinationStatus,
  getDestinationSubscription,
  getDestinationSubscriptionPreview,
  listDestinationOptions,
  listDestinations,
  listDestinationSubscriptions,
  listDestinationExports,
  sendDestinationTestEvent,
  updateDestination,
  updateDestinationSubscription,
  validateDestinationSettings,
  validateDestinationOptionSettings,
  toggleDestinationSubscription,
  setAutotrackFlag,
} from './api';
import type {
  Destination,
  DestinationErrorType,
  DestinationEvent,
  DestinationOption,
  DestinationSubscription,
} from '.';
import type { ComputedStatus, StatusDetails } from '@/features/pipelines/utils';
import { computeStatus } from '@/features/pipelines/utils';
import { keyBy } from 'lodash-es';
import type { Source } from '../sources';
import { useParams } from '@/services/router/compat';
import type { MetricsDuration } from '@/utils/time';
import type { PaginationRequest } from '@/hooks/pipelines/use-paginated-query';
import { usePaginatedQuery } from '@/hooks/pipelines/use-paginated-query';
import type { AxiosError } from 'axios';
import { useCopilotContext } from '@customerio/hydra-copilot/react';
import { useOAuthUpgrade } from '@/features/pipelines/oauth';
import { isEmptyString } from '@/utils/string';

type NullableDestinationId = DestinationId | null | undefined;

export const useDestinationIdFromParams = () => {
  const { destinationId } = useParams();
  if (destinationId == null) {
    throw new Error('This hook should be used inside /destinations/:destinationId route');
  }
  if (destinationId !== 'journeys') {
    return Number.parseInt(destinationId);
  }
  return destinationId;
};

export const useListDestinationOptions = (workspaceId: number | null) => {
  return useQuery({
    queryKey: ['destinationOptions', { workspaceId }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return listDestinationOptions(workspaceId);
    },
    enabled: workspaceId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export const useListDestinationOptionsMap = (workspaceId: number | null) => {
  return useQuery({
    queryKey: ['destinationOptions', { workspaceId }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return listDestinationOptions(workspaceId);
    },
    enabled: workspaceId != null,
    select: (options: DestinationOption[]) => {
      return keyBy(options, 'option_id');
    },
    staleTime: 60 * 1000, // one minute
  });
};

export const useGetDestination = (workspaceId: number | null, destinationId: NullableDestinationId) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return getDestination(workspaceId, destinationId);
    },
    enabled: workspaceId != null && destinationId != null,
    staleTime: 30 * 1000,
    initialData: () => {
      if (workspaceId == null) {
        return undefined;
      }

      return queryClient
        .getQueryData<Destination[]>(['destinations', 'list', { workspaceId }])
        ?.find((destination: Destination) => {
          return destination.id === destinationId;
        });
    },
    initialDataUpdatedAt: () => {
      if (workspaceId == null) {
        return undefined;
      }

      return queryClient.getQueryState(['destinations', 'list', { workspaceId }])?.dataUpdatedAt;
    },
  });
};

export const useCreateDestination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      destination,
    }: {
      workspaceId: number;
      destination: {
        name: string;
        enabled?: boolean;
        enable_actions?: boolean;
        optionId: string;
        replayData?: boolean;
        sourceIds?: number[];
        // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
        settings: Record<string, any>;
      };
    }) => {
      return createDestination(workspaceId, {
        ...destination,
        name: (destination.name ?? '').trim(),
      });
    },
    onSuccess: (dbDestination, { workspaceId }) => {
      queryClient.setQueryData(['destinations', { id: dbDestination.id, workspaceId }], dbDestination);
      return queryClient.invalidateQueries({
        queryKey: ['destinations', 'list', { workspaceId }],
      });
    },
    onError: notifyOnError,
  });
};

export const useUpdateDestination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      workspaceId: number;
      destinationId: number;
      destination: {
        name: string;
        enabled: boolean;
        // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
        settings: Record<string, any>;
      };
    }) => {
      return updateDestination(data.workspaceId, data.destinationId, {
        ...data.destination,
        name: (data.destination.name ?? '').trim(),
      });
    },
    onSuccess: (dbDestination, { workspaceId }) => {
      const currentDestination = queryClient.getQueryData<Destination>([
        'destinations',
        { id: dbDestination.id, workspaceId: workspaceId },
      ]);

      // If the destination was enabled or disabled, invalidate the status query
      // so that the status gets refetched
      if (currentDestination != null && currentDestination.enabled !== dbDestination.enabled) {
        queryClient.invalidateQueries({
          queryKey: ['destinations', { id: dbDestination.id, workspaceId: workspaceId }, 'status'],
        });
      }

      queryClient.setQueryData(['destinations', { id: dbDestination.id, workspaceId }], dbDestination);
      queryClient.setQueriesData(
        { queryKey: ['destinations', 'list', { workspaceId }] },
        (destinations: Destination[] | undefined) => {
          if (!Array.isArray(destinations)) return destinations;
          return destinations.map((oldDestination) => {
            if (oldDestination.id === dbDestination.id) {
              return dbDestination;
            }

            return oldDestination;
          });
        },
      );
      return queryClient.invalidateQueries({
        queryKey: ['destinations', { id: dbDestination.id, workspaceId: workspaceId }, 'connect'],
      });
    },
    onError: notifyOnError,
  });
};

export const useDeleteDestination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, destinationId }: { workspaceId: number; destinationId: number }) => {
      return deleteDestination(workspaceId, destinationId);
    },
    // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
    onSuccess: (_data: any, { workspaceId, destinationId }: { workspaceId: number; destinationId: number }) => {
      queryClient.removeQueries({ queryKey: ['destinations', { id: destinationId, workspaceId }] });
      queryClient.setQueriesData(
        { queryKey: ['destinations', 'list', { workspaceId }] },
        (destinations: Destination[] | undefined) => {
          if (!Array.isArray(destinations)) return destinations;
          return destinations.filter((destination) => {
            return destination.id !== destinationId;
          });
        },
      );

      // remove the deleted destination from any source
      queryClient.setQueriesData(
        { queryKey: ['sources', 'list', { workspaceId }] },
        (sources: Source[] | undefined) => {
          if (!Array.isArray(sources)) return sources;
          return sources.map((source) => {
            if (source.destination_ids.includes(destinationId)) {
              source.destination_ids = source.destination_ids.filter((id) => id !== destinationId);
            }
            return source;
          });
        },
      );
    },
    onError: notifyOnError,
  });
};

export const useGetDestinationMetrics = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
  duration?: MetricsDuration,
) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'metrics', { duration }],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return getDestinationMetrics(workspaceId, destinationId, duration);
    },
    enabled: workspaceId != null && destinationId != null,
  });
};

export const useGetDestinationEvents = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
  params: {
    sourceId?: number | null;
    limit?: number;
    continuation?: ContinuationRequest;
    filter_status_codes?: string[];
    subscription_ids?: number[];
    search_text?: string;
  },
  options?: {
    enabled?: boolean;
  },
) => {
  return useInfiniteQuery({
    retry: false, // Disable React Query retries, we'll handle it ourselves
    gcTime: 0, // Don't cache empty polling results - fresh data on every poll
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    getPreviousPageParam: getPreviousPageParam as (
      firstPage: { events: DestinationEvent[]; meta: ContinuationMeta },
      allPages: { events: DestinationEvent[]; meta: ContinuationMeta }[],
    ) => string | undefined,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    getNextPageParam: getNextPageParam as (lastPage: {
      events: DestinationEvent[];
      meta: ContinuationMeta;
    }) => string | undefined,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    initialPageParam: undefined as string | undefined,
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'events', params],
    queryFn: async ({ pageParam }): Promise<{ events: DestinationEvent[]; meta: ContinuationMeta }> => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      if (!isEmptyString(pageParam)) {
        params.continuation = pageParam.startsWith('-') ? { before: pageParam.slice(1) } : { after: pageParam };
      }

      const result = await getDestinationEvents(workspaceId, destinationId, params);

      return result;
    },
    enabled: workspaceId != null && destinationId != null && (options?.enabled ?? true),
  });
};

export const useListDestinations = (
  workspaceId: number | null,
  filters?: { sourceId?: number | null; inverse?: boolean | null },
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [
      'destinations',
      'list',
      {
        workspaceId,
        sourceId: filters?.sourceId ?? undefined,
        inverse: filters?.inverse ?? undefined,
      },
    ],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return listDestinations(workspaceId, filters);
    },
    enabled: workspaceId != null,
    staleTime: 30 * 1000,
    initialData: () => {
      if (workspaceId == null) {
        return undefined;
      }

      if (filters?.sourceId == null) {
        return undefined;
      }

      const sourceId = filters.sourceId;
      const inverse = filters?.inverse ?? false;

      // If querying for destinations for a specific source, we can use the
      // previously fetched destinations and filter them, if available
      return queryClient
        .getQueryData<Destination[]>(['destinations', 'list', { workspaceId }])
        ?.filter((destination: Destination) => {
          const hasSource = destination.source_ids.includes(sourceId);
          return inverse ? !hasSource : hasSource;
        });
    },
  });
};

export const useSendDestinationTestEvent = () => {
  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      payload,
    }: {
      workspaceId: number;
      destinationId: number;
      // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
      payload: Record<string, any>;
    }) => {
      return sendDestinationTestEvent(workspaceId, destinationId, payload);
    },
    onError: notifyOnError,
  });
};

export const useGetDestinationOptionManifest = (
  workspaceId: number | null,
  destinationOptionId: string | null | undefined,
) => {
  return useQuery({
    queryKey: ['destinationOptions', 'manifest', { id: destinationOptionId, workspaceId }],
    queryFn: async () => {
      if (workspaceId == null || destinationOptionId == null) {
        return Promise.reject();
      }
      return getDestinationOptionManifest(workspaceId, destinationOptionId);
    },
    enabled: workspaceId != null && destinationOptionId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export const useListDestinationSubscriptions = (workspaceId: number | null, destinationId: NullableDestinationId) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', 'list'],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return listDestinationSubscriptions(workspaceId, destinationId);
    },
    enabled: workspaceId != null && destinationId != null,
  });
};

export const useGetDestinationSubscription = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
  subscriptionId: number | null,
) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', { id: subscriptionId }],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null || subscriptionId == null) {
        return Promise.reject();
      }
      return getDestinationSubscription(workspaceId, destinationId, subscriptionId);
    },
    enabled: workspaceId != null && destinationId != null && subscriptionId != null,
  });
};

export const useCreateDestinationSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      subscription,
    }: {
      workspaceId: number;
      destinationId: number;
      subscription: {
        action: string;
        mapping: string;
        name: string;
        subscribe: string;
        // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
        settings?: Record<string, any>;
      };
    }) => {
      return createDestinationSubscription(workspaceId, destinationId, subscription);
    },
    onSuccess: (dbSubscription, { workspaceId, destinationId }) => {
      queryClient.setQueryData(
        ['destinations', { id: destinationId, workspaceId }, 'subscriptions', { id: dbSubscription.id }],
        dbSubscription,
      );
      return queryClient.invalidateQueries({
        queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', 'list'],
      });
    },
    onError: notifyOnError,
  });
};

export const useUpdateDestinationSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      subscriptionId,
      subscription,
    }: {
      workspaceId: number;
      destinationId: number;
      subscriptionId: number;
      subscription: {
        action: string;
        enabled: boolean;
        mapping: string;
        name: string;
        subscribe: string;
        // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
        settings?: Record<string, any>;
      };
    }) => {
      return updateDestinationSubscription(workspaceId, destinationId, subscriptionId, subscription);
    },
    onSuccess: (dbSubscription, { destinationId, workspaceId }) => {
      queryClient.setQueryData(
        ['destinations', { id: destinationId, workspaceId }, 'subscriptions', { id: dbSubscription.id }],
        dbSubscription,
      );
      queryClient.setQueriesData(
        { queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', 'list'] },
        (subscriptions: DestinationSubscription[] | undefined) => {
          if (!Array.isArray(subscriptions)) return subscriptions;
          return subscriptions.map((oldSubscription) => {
            if (oldSubscription.id === dbSubscription.id) {
              return dbSubscription;
            }

            return oldSubscription;
          });
        },
      );
    },
    onError: notifyOnError,
  });
};

export const useToggleDestinationSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      enabled,
    }: {
      workspaceId: number;
      destinationId: number;
      enabled: boolean;
    }) => {
      return toggleDestinationSubscription(workspaceId, destinationId, enabled);
    },
    onSuccess: (dbSubscriptions, { destinationId, workspaceId }) => {
      for (const subscription of dbSubscriptions) {
        queryClient.invalidateQueries({
          queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', { id: subscription.id }],
        });
      }
      return queryClient.invalidateQueries({
        queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', 'list'],
      });
    },
    onError: notifyOnError,
  });
};

export const useDeleteDestinationSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      subscriptionId,
    }: {
      workspaceId: number;
      destinationId: number;
      subscriptionId: number;
    }) => {
      return deleteDestinationSubscription(workspaceId, destinationId, subscriptionId);
    },
    onSuccess: (
      // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
      _data: any,
      {
        workspaceId,
        destinationId,
        subscriptionId,
      }: { workspaceId: number; destinationId: number; subscriptionId: number },
    ) => {
      queryClient.removeQueries({
        queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', { id: subscriptionId }],
      });
      queryClient.setQueriesData(
        { queryKey: ['destinations', { id: destinationId, workspaceId }, 'subscriptions', 'list'] },
        (subscriptions: DestinationSubscription[] | undefined) => {
          if (!Array.isArray(subscriptions)) return subscriptions;
          return subscriptions.filter((subscription) => {
            return subscription.id !== subscriptionId;
          });
        },
      );
    },
    onError: notifyOnError,
  });
};

export const useGetDestinationSubscriptionPreview = (
  workspaceId: number | null,
  {
    mapping,
    event,
  }: {
    // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
    mapping: Record<string, any>;
    // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
    event: Record<string, any>;
  },
) => {
  return useQuery({
    queryKey: ['subscriptions', 'preview', { mapping, event }],
    gcTime: 0,
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return getDestinationSubscriptionPreview(workspaceId, { mapping, event });
    },
  });
};

export const useGetDestinationStatus = (workspaceId: number | null, destinationId: NullableDestinationId) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'status'],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return getDestinationStatus(workspaceId, destinationId);
    },
    enabled: workspaceId != null && destinationId != null,
  });
};

export type DestinationInfo = Omit<Destination, 'settings'>;

export const useComputeDestinationsStatuses = (workspaceId?: number | null, destinations?: DestinationInfo[]) => {
  return useQuery({
    queryKey: ['destinations', { workspaceId: workspaceId, destinations: destinations }, 'statuses'],
    queryFn: async () => {
      if (workspaceId == null || destinations == null || destinations.length === 0) {
        return Promise.reject();
      }
      return getDestinationsLastSeen(workspaceId).then((destinationsLastSeen) => {
        const statuses: Record<number, StatusDetails> = {};

        for (const destination of destinations) {
          const lastSeen = destinationsLastSeen[`${destination.id}`];
          const status = computeStatus(destination.enabled, lastSeen ?? 0);

          statuses[destination.id] = {
            // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
            status: status as ComputedStatus,
            lastSeen,
          };
        }
        return statuses;
      });
    },
    enabled: workspaceId != null && destinations != null && destinations.length > 0,
  });
};

export const useGetDestinationErrorsSummary = (workspaceId: number | null, destinationId: NullableDestinationId) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'errorsSummary'],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return getDestinationErrorsSummary(workspaceId, destinationId);
    },
    enabled: workspaceId != null && destinationId != null,
  });
};

export const useGetDestinationErrorSamples = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
  type: DestinationErrorType,
) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'errors', 'sample', type],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return getDestinationErrorSamples(workspaceId, destinationId, type);
    },
    enabled: workspaceId != null && destinationId != null,
  });
};

export const useGetDestinationEventById = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
  eventId?: string,
  params: {
    storeServerId?: string;
    storeKey?: string;
    truncate?: boolean;
  } = {},
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: [
      'destinations',
      { id: destinationId, workspaceId: workspaceId },
      'events',
      eventId,
      'storeServerId',
      params.storeServerId,
      'storeKey',
      params.storeKey,
      'truncate',
      params.truncate,
    ],
    queryFn: async () => {
      if (
        workspaceId == null ||
        destinationId == null ||
        eventId == null ||
        params.storeServerId == null ||
        params.storeKey == null
      ) {
        return Promise.reject();
      }
      return getDestinationEventById(workspaceId, destinationId, eventId, params);
    },
    enabled:
      workspaceId != null &&
      destinationId != null &&
      eventId != null &&
      params.storeServerId != null &&
      params.storeKey != null &&
      (options?.enabled ?? true),
  });
};

export const useGetDestinationOAuthConnection = (workspaceId: number | null, destinationId: NullableDestinationId) => {
  return useQuery({
    queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'connect'],
    queryFn: async () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }
      return getDestinationOAuthConnection(workspaceId, destinationId);
    },
    enabled: workspaceId != null && destinationId != null,
  });
};

export const useGetDestinationOAuthProviderConfig = (
  workspaceId: number | null,
  optionId: string | null,
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  settings: Record<string, any> | null,
) => {
  return useQuery({
    queryKey: ['destinations', 'connect', { optionId: optionId, workspaceId: workspaceId, settings }],
    queryFn: async () => {
      if (workspaceId == null || optionId == null) {
        return Promise.reject();
      }
      return getDestinationOAuthProviderConfig(workspaceId, optionId, settings);
    },
    enabled: workspaceId != null && optionId != null,
    retry: (_, error: AxiosError) => error?.response?.status !== 400,
  });
};

export const useCreateDestinationOAuthConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      payload,
    }: {
      workspaceId: number;
      destinationId: number;
      // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
      payload: Record<string, any>;
    }) => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }

      return createDestinationOAuthConnection(workspaceId, destinationId, payload);
    },
    onSuccess: (_, { workspaceId, destinationId }) => {
      const destinationKey = ['destinations', { id: destinationId, workspaceId: workspaceId }];
      const destinationListKey = ['destinations', 'list', { workspaceId }];
      const connectionsKey = ['destinations', { id: destinationId, workspaceId: workspaceId }, 'connect'];
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: destinationKey }),
        queryClient.invalidateQueries({ queryKey: destinationListKey }),
        queryClient.invalidateQueries({ queryKey: connectionsKey }),
      ]);
    },
    onError: notifyOnError,
  });
};

export const useDeleteDestinationOAuthConnection = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }

      return deleteDestinationOAuthConnection(workspaceId, destinationId);
    },
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['destinations', { id: destinationId, workspaceId: workspaceId }, 'connect'],
      });

      const destinationKey = ['destinations', { id: destinationId, workspaceId: workspaceId }];
      const destinationListKey = ['destinations', 'list', { workspaceId }];
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: destinationKey }),
        queryClient.invalidateQueries({ queryKey: destinationListKey }),
      ]);
    },
    onError: notifyOnError,
  });
};

export const useListDestinationExports = (
  workspaceId: number | null,
  destinationId: number | null,
  params: {
    page?: number;
    limit?: number;
  } = {},
) => {
  return usePaginatedQuery(
    {
      gcTime: 0,
      queryKey: ['destinations', { id: destinationId, workspaceId }, 'exports', 'list'],
      queryFn: async ({ meta }) => {
        if (workspaceId == null || destinationId == null) {
          return Promise.reject();
        }
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        return listDestinationExports(workspaceId, destinationId, meta?.pagination as PaginationRequest);
      },
      enabled: workspaceId != null && destinationId != null,
    },
    {
      initialPage: params.page,
      pageSize: params.limit,
    },
  );
};

export const useValidateDestinationSettings = () => {
  return useMutation({
    mutationFn: ({
      workspaceId,
      destinationId,
      settings,
    }: {
      workspaceId: number;
      destinationId: number;
      // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
      settings: Record<string, any>;
    }) => {
      if (workspaceId == null || destinationId == null) {
        return Promise.reject();
      }

      return validateDestinationSettings(workspaceId, destinationId, settings);
    },
    onError: notifyOnError,
  });
};

export const useValidateDestinationOptionSettings = () => {
  return useMutation({
    mutationFn: ({
      workspaceId,
      optionId,
      settings,
    }: {
      workspaceId: number;
      optionId: string;
      // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
      settings: Record<string, any>;
    }) => {
      if (workspaceId == null || optionId == null) {
        return Promise.reject();
      }

      return validateDestinationOptionSettings(workspaceId, optionId, settings);
    },
  });
};

// Temporary method for backwards-compatibility. Can be removed once https://github.com/customerio/cdp/pull/3176 has been deployed for 24h.
export const useGetDestinationErrorByErrorIdOrEventId = (
  workspaceId: number | null,
  destinationId: NullableDestinationId,
  eventId?: string,
  errorType?: DestinationErrorType,
  params: {
    storeServerId?: string;
    storeKey?: string;
    truncate?: boolean;
  } = {},
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: [
      'destinations',
      { id: destinationId, workspaceId: workspaceId },
      'events',
      eventId,
      'storeServerId',
      params.storeServerId,
      'storeKey',
      params.storeKey,
      'truncate',
      params.truncate,
    ],
    queryFn: async () => {
      if (
        workspaceId == null ||
        destinationId == null ||
        eventId == null ||
        errorType == null ||
        params.storeServerId == null ||
        params.storeKey == null
      ) {
        return Promise.reject();
      }

      try {
        // Fetch from fdb (dest_indexer) first, if available.
        return await getDestinationErrorById(workspaceId, destinationId, eventId, errorType, params);
      } catch (_error) {
        // Otherwise, fetch from kmq.
        return await getDestinationEventById(workspaceId, destinationId, eventId, params);
      }
    },
    enabled:
      workspaceId != null &&
      destinationId != null &&
      eventId != null &&
      params.storeServerId != null &&
      params.storeKey != null &&
      (options?.enabled ?? true),
    retry: (_, error: AxiosError) => error?.response?.status !== 404,
  });
};

export function useExplainDestinationErrorInChat(
  contextData: (
    | {
        value: Record<string, string> | undefined;
        description: string;
      }
    | {
        value: string | null | undefined;
        description: string;
      }
  )[],
) {
  const instructions =
    'Analyze this HTTP request and response data and explain the error in simple terms. If possible, identify the root cause and provide a solution to the problem.';

  const [requestHeadersContext, requestBodyContext, responseHeadersContext, responseBodyContext] = contextData;

  useCopilotContext({
    value: requestHeadersContext?.value,
    description: requestHeadersContext?.description,
    isEnabled: true,
  });

  useCopilotContext({
    value: requestBodyContext?.value,
    description: requestBodyContext?.description,
    isEnabled: true,
  });

  useCopilotContext({
    value: responseHeadersContext?.value,
    description: responseHeadersContext?.description,
    isEnabled: true,
  });

  useCopilotContext({
    value: responseBodyContext?.value,
    description: responseBodyContext?.description,
    isEnabled: true,
  });

  return useMutation({
    mutationKey: ['destinationErrorAnalysis'],
    mutationFn: async () => {
      // Send the message to the copilot
      // Note if you await this, the sidecar will not open until the message is sent
      window?.__hydra_copilot__?.currentCopilotSession?.get()?.sendMessage(instructions);

      // Open the assistant sidebar
      return window?.__hydra_sidebar__?.open('copilot');
    },
  });
}

export const useDestinationOAuthUpgrade = (
  workspaceId: number,
  destination: Destination,
  settings?: Record<string, unknown>,
) => {
  const providerConfig = useGetDestinationOAuthProviderConfig(
    workspaceId,
    destination.option_id,
    settings ?? destination.settings ?? null,
  );
  const doUpdateDestination = useUpdateDestination();
  const doCreateOAuthConnection = useCreateDestinationOAuthConnection();

  return useOAuthUpgrade({
    optionId: destination.option_id,
    providerConfig,
    updateEntity: () =>
      doUpdateDestination.mutateAsync({
        workspaceId,
        destinationId: destination.id,
        destination: {
          name: destination.name,
          enabled: destination.enabled,
          settings: settings ?? destination.settings ?? {},
        },
      }),
    createConnection: (response) =>
      doCreateOAuthConnection.mutateAsync({
        workspaceId,
        destinationId: destination.id,
        payload: response,
      }),
    onError: notifyOnError,
  });
};

export { forceDestinationOAuthTokenRefresh } from './api';
export { sortByLastSeenAndStatus } from './utils/connections';

export const useSetAutotrackFlag = (accountId: number | null) => {
  const account = useStore(accountStore);

  return useMutation({
    mutationFn: (enable: boolean) => {
      if (accountId == null) {
        return Promise.reject();
      }
      return setAutotrackFlag(accountId, enable);
    },
    onSuccess({ feature_flags }) {
      if (account != null) {
        const updatedAccount = {
          ...account,
          feature_flags,
        };
        setAccountStore(updatedAccount);
      }
    },
    onError: notifyOnError,
  });
};
